/**
 * FornadaItensManager Component
 */

import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { FornadaItemDialog } from "./FornadaItemDialog";
import type { FornadaItem } from "@/types/fornada";

interface FornadaItensManagerProps {
  itens: FornadaItem[];
  onItensChange: (itens: FornadaItem[]) => void;
}

export function FornadaItensManager({ itens, onItensChange }: FornadaItensManagerProps) {
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<FornadaItem | null>(null);

  const handleAddItem = () => {
    setEditingItem(null);
    setShowItemDialog(true);
  };

  const handleEditItem = (item: FornadaItem) => {
    setEditingItem(item);
    setShowItemDialog(true);
  };

  const handleDeleteItem = (id: string) => {
    onItensChange(itens.filter(item => item.id_fornada_item !== id));
  };

  const handleSaveItem = (item: FornadaItem) => {
    if (editingItem) {
      onItensChange(itens.map(i => i.id_fornada_item === item.id_fornada_item ? item : i));
    } else {
      onItensChange([...itens, { ...item, id_fornada_item: crypto.randomUUID() }]);
    }
    setShowItemDialog(false);
  };

  return (
    <div className="space-y-4">
      <Button type="button" variant="outline" onClick={handleAddItem} className="w-full">
        <Plus className="w-4 h-4 mr-2" />
        Adicionar Receita
      </Button>

      {itens.length > 0 && (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Receita</TableHead>
                <TableHead className="text-right">Quantidade</TableHead>
                <TableHead className="text-right">Custo Total</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {itens.map((item) => (
                <TableRow key={item.id_fornada_item}>
                  <TableCell>{item.receita_nome}</TableCell>
                  <TableCell className="text-right">{item.quantidade_produzida}</TableCell>
                  <TableCell className="text-right">R$ {item.custo_total_item.toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEditItem(item)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteItem(item.id_fornada_item)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <FornadaItemDialog
        open={showItemDialog}
        onOpenChange={setShowItemDialog}
        item={editingItem}
        onSave={handleSaveItem}
      />
    </div>
  );
}
